
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Eye, EyeOff, ArrowLeft, Settings, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

const HQLogin = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { signIn } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      if (error) {
        toast({
          title: "Login Failed",
          description: error.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Welcome back!",
          description: "Redirecting to HQ dashboard...",
        });
        navigate('/app/hq/dashboard');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      <Card className="w-full max-w-md bg-slate-800/90 backdrop-blur-sm shadow-xl border border-amber-500/20">
        <CardHeader className="text-center space-y-4">
          <div className="flex items-center justify-center mb-4">
            <Link 
              to="/login"
              className="absolute left-6 p-2 rounded-lg hover:bg-slate-700/50 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-amber-200" />
            </Link>
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 relative">
              <Settings className="text-white w-8 h-8" />
              <Shield className="absolute -top-1 -right-1 w-6 h-6 text-amber-200" />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl font-bold text-white">
              HQ Operator Portal
            </CardTitle>
            <p className="text-amber-200 mt-2">
              Access launch kits, system analytics, SOPs, and AI agent tools across all JUV locations.
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-amber-200 font-medium">Email</Label>
              <div className="relative">
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border-amber-500/30 bg-slate-700/50 text-white placeholder:text-slate-400 focus:border-amber-500 focus:ring-amber-500/20 pl-10"
                  required
                />
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-amber-400" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-amber-200 font-medium">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="border-amber-500/30 bg-slate-700/50 text-white placeholder:text-slate-400 focus:border-amber-500 focus:ring-amber-500/20 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-70 text-amber-400"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full text-white hover:opacity-90 transition-all duration-300 bg-gradient-to-r from-amber-500 to-orange-600"
            >
              {loading ? 'Authenticating...' : 'Log In as HQ Operator'}
            </Button>
          </form>

          <div className="text-center text-sm text-slate-400">
            <Shield className="inline w-4 h-4 mr-1" />
            Restricted to authorized JUV admin staff only.
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HQLogin;
